'use client';

import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Building2,
  CreditCard,
  Headphones,
  MessageSquare,
  PlayCircle,
  Settings,
  Users,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

// Particle Background Component
function ParticleBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const particles: Array<{
      x: number;
      y: number;
      dx: number;
      dy: number;
      size: number;
    }> = [];
    const particleCount = 50;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const createParticle = () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      dx: (Math.random() - 0.5) * 0.5,
      dy: (Math.random() - 0.5) * 0.5,
      size: Math.random() * 2 + 1,
    });

    const initParticles = () => {
      for (let i = 0; i < particleCount; i++) {
        particles.push(createParticle());
      }
    };

    const drawParticle = (particle: (typeof particles)[0]) => {
      if (!ctx) return;

      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(254, 74, 11, 0.1)';
      ctx.fill();
    };

    const updateParticle = (particle: (typeof particles)[0]) => {
      particle.x += particle.dx;
      particle.y += particle.dy;

      if (particle.x < 0 || particle.x > canvas.width) particle.dx *= -1;
      if (particle.y < 0 || particle.y > canvas.height) particle.dy *= -1;
    };

    const animate = () => {
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle) => {
        drawParticle(particle);
        updateParticle(particle);
      });

      requestAnimationFrame(animate);
    };

    resizeCanvas();
    initParticles();
    animate();

    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 -z-10 h-full w-full bg-gradient-to-br from-orange-50 to-white"
      aria-hidden="true"
    />
  );
}

// Animated Counter Component
function AnimatedCounter({
  end,
  duration = 2000,
  suffix = '',
}: {
  end: number;
  duration?: number;
  suffix?: string;
}) {
  const [count, setCount] = useState(0);
  const countRef = useRef(count);
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      const startTime = Date.now();
      const endValue = end;

      const updateCount = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const currentCount = Math.floor(endValue * progress);
        if (currentCount !== countRef.current) {
          setCount(currentCount);
          countRef.current = currentCount;
        }

        if (progress < 1) {
          requestAnimationFrame(updateCount);
        }
      };

      requestAnimationFrame(updateCount);
    }
  }, [end, duration, inView]);

  return (
    <span ref={ref} className="tabular-nums">
      {count.toLocaleString()}
      {suffix}
    </span>
  );
}

// Feature Card Component
function FeatureCard({
  title,
  description,
  icon: Icon,
}: {
  title: string;
  description: string;
  icon: any;
}) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="group relative overflow-hidden rounded-lg border bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 text-[#fe4a0b] group-hover:bg-[#fe4a0b] group-hover:text-white">
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="mb-2 text-lg font-semibold">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-transparent via-orange-50/50 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
    </motion.div>
  );
}

// Logo Slider Component
function LogoSlider() {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setOffset((prev) => (prev + 1) % 200);
    }, 50);

    return () => clearInterval(interval);
  }, []);

  const logos = Array.from({ length: 8 }, (_, i) => ({
    src: `https://hupo.co/logos/Hupo_Logotype_Orange(noR).svg`,
    alt: `Company ${i + 1}`,
  }));

  return (
    <div className="relative overflow-hidden py-8">
      <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-20 bg-gradient-to-r from-gray-50 to-transparent" />
      <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-20 bg-gradient-to-l from-gray-50 to-transparent" />
      <motion.div
        className="flex gap-8"
        animate={{ x: -offset }}
        transition={{ duration: 0, ease: 'linear' }}
      >
        {[...logos, ...logos].map((logo, i) => (
          <div key={i} className="flex-shrink-0">
            <img
              src={logo.src || '/placeholder.svg'}
              alt={logo.alt}
              width={120}
              height={40}
              className="h-10 w-auto grayscale transition-all hover:grayscale-0"
            />
          </div>
        ))}
      </motion.div>
    </div>
  );
}

// Main Page Component
export default function Page() {
  return (
    <div className="relative min-h-screen">
      <ParticleBackground />

      {/* Hero Section */}
      <section className="relative overflow-hidden px-4 pt-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8">
            <motion.div
              className="sm:text-center lg:col-span-6 lg:text-left"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="mb-8">
                <img
                  src="https://hupo.co/logos/Hupo_Logotype_Orange(noR).svg"
                  alt="Hupo Logo"
                  width={120}
                  height={40}
                  className="h-10 w-auto"
                />
              </div>
              <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
                <span className="block">Master customer support</span>
                <span className="block text-[#fe4a0b]">
                  through realistic AI roleplay
                </span>
              </h1>
              <p className="mt-3 text-base text-gray-500 sm:mx-auto sm:mt-5 sm:max-w-xl sm:text-lg lg:mx-0">
                Transform your customer service team with AI-powered training.
                Practice real scenarios, get instant feedback, and improve your
                skills with our interactive platform.
              </p>
              <div className="mt-8 sm:flex sm:justify-center lg:justify-start">
                <Button
                  size="lg"
                  className="w-full bg-[#fe4a0b] text-white hover:bg-[#fe4a0b]/90 sm:w-auto"
                  onClick={() => {
                    window.location.href = '/customer/categories';
                  }}
                >
                  Try for Free
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="mt-3 w-full sm:ml-3 sm:mt-0 sm:w-auto"
                >
                  Watch Demo
                </Button>
              </div>
            </motion.div>
            <motion.div
              className="relative mt-12 lg:col-span-6 lg:mt-0"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="relative mx-auto w-full rounded-lg shadow-lg lg:max-w-md mt-36">
                <div className="relative block w-full overflow-hidden rounded-lg">
                  <video
                    src="https://dl1186a8rusj2.cloudfront.net/videos/compressed_Hero.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="mt-20 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-8 text-center sm:grid-cols-3">
            <div className="rounded-lg bg-white p-8 shadow-sm">
              <p className="text-4xl font-bold text-[#fe4a0b]">
                <AnimatedCounter end={25000} suffix="+" />
              </p>
              <p className="mt-2 text-sm text-gray-600">
                Practice Sessions Completed
              </p>
            </div>
            <div className="rounded-lg bg-white p-8 shadow-sm">
              <p className="text-4xl font-bold text-[#fe4a0b]">
                <AnimatedCounter end={32} suffix="%" />
              </p>
              <p className="mt-2 text-sm text-gray-600">
                Average CSAT Improvement
              </p>
            </div>
            <div className="rounded-lg bg-white p-8 shadow-sm">
              <p className="text-4xl font-bold text-[#fe4a0b]">
                <AnimatedCounter end={50} suffix="+" />
              </p>
              <p className="mt-2 text-sm text-gray-600">Enterprise Clients</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="mt-20 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Training Modules
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-xl text-gray-500">
              Comprehensive training scenarios designed for various customer
              support roles
            </p>
          </div>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <FeatureCard
              title="Hotel Services"
              description="Handle booking inquiries, room service requests, and guest complaints effectively"
              icon={Building2}
            />
            <FeatureCard
              title="Technical Support"
              description="Troubleshoot common technical issues and provide clear solutions"
              icon={Settings}
            />
            <FeatureCard
              title="Billing Support"
              description="Address payment concerns and explain billing processes professionally"
              icon={CreditCard}
            />
            <FeatureCard
              title="Customer Service"
              description="Master general customer service skills and best practices"
              icon={Users}
            />
            <FeatureCard
              title="Live Chat Support"
              description="Excel in providing quick and effective written responses"
              icon={MessageSquare}
            />
            <FeatureCard
              title="Phone Support"
              description="Develop excellent verbal communication and problem-solving skills"
              icon={Headphones}
            />
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="mt-20 bg-gray-50 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Trusted by Industry Leaders
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-xl text-gray-500">
              Join forward-thinking companies improving their customer support
              with AI training
            </p>
          </div>
          <LogoSlider />
          <div className="mt-16">
            <div className="rounded-2xl bg-white p-8 shadow-sm">
              <div className="grid gap-8 lg:grid-cols-3">
                <div className="flex flex-col justify-between">
                  <div>
                    <BarChart3 className="h-12 w-12 text-[#fe4a0b]" />
                    <p className="mt-4 text-4xl font-bold text-gray-900">
                      <AnimatedCounter end={42} suffix="%" />
                    </p>
                    <p className="mt-2 text-sm text-gray-600">
                      Reduction in Training Time
                    </p>
                  </div>
                </div>
                <blockquote className="lg:col-span-2">
                  <p className="text-xl text-gray-900">
                    "The AI roleplay platform has transformed how we train our
                    customer support team. Our agents are more confident and
                    better prepared for real customer interactions."
                  </p>
                  <footer className="mt-6">
                    <p className="font-medium text-gray-900">Sarah Johnson</p>
                    <p className="text-sm text-gray-500">
                      Customer Support Director, Fortune 500 Company
                    </p>
                  </footer>
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="mt-20 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="relative overflow-hidden rounded-2xl bg-[#fe4a0b] px-6 py-20 shadow-xl sm:px-12 sm:py-24">
            <div className="relative">
              <div className="sm:text-center">
                <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  Ready to transform your customer support?
                </h2>
                <p className="mx-auto mt-6 max-w-2xl text-lg text-orange-100">
                  Start training your team with AI-powered scenarios today and
                  see the difference in your customer satisfaction scores.
                </p>
              </div>
              <div className="mt-8 sm:flex sm:justify-center">
                <Button
                  size="lg"
                  className="w-full bg-white text-[#fe4a0b] hover:bg-orange-50 sm:w-auto"
                  onClick={() => {
                    window.location.href = '/customer/categories';
                  }}
                >
                  Try for Free
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="mt-3 w-full border-white text-white hover:bg-[#fe4a0b]/90 sm:ml-3 sm:mt-0 sm:w-auto"
                >
                  Schedule Demo
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
